import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';

// Image resize and base64 utilities
const resizeImage = (file, maxSizeKB = 50) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (max 200x150 for flags)
      const maxWidth = 200;
      const maxHeight = 150;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Start with high quality and reduce if needed
      let quality = 0.9;
      let dataURL = canvas.toDataURL('image/jpeg', quality);
      
      // Calculate file size and reduce quality if needed
      const getBase64Size = (base64String) => {
        const stringLength = base64String.length - 'data:image/jpeg;base64,'.length;
        return Math.round(stringLength * 0.75);
      };
      
      while (getBase64Size(dataURL) > maxSizeKB * 1024 && quality > 0.1) {
        quality -= 0.1;
        dataURL = canvas.toDataURL('image/jpeg', quality);
      }
      
      resolve(dataURL);
    };
    
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const convertURLToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    throw new Error('Failed to convert URL to base64');
  }
};

const getBase64Size = (base64String) => {
  const stringLength = base64String.length - 'data:image/jpeg;base64,'.length;
  return Math.round(stringLength * 0.75);
};

const isValidImageURL = (url) => {
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp)(\?.*)?$/i;
  return imageExtensions.test(url);
};

const FlagManagement = () => {
  const [flags, setFlags] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    imageUrl: ''
  });

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/flags`,{
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFlags(data);
      }
    } catch (error) {
      console.error('Error fetching flags:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || (!formData.image && !formData.imageUrl)) {
      setMessage('Please fill in all required fields');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      let imageData = formData.image;
      let imageUrl = formData.imageUrl;
      let size = 0;

      // If URL is provided, convert to base64
      if (formData.imageUrl && !formData.image) {
        if (!isValidImageURL(formData.imageUrl)) {
          setMessage('Please provide a valid image URL');
          setUploading(false);
          return;
        }
        imageData = await convertURLToBase64(formData.imageUrl);
        size = getBase64Size(imageData);
      } else if (formData.image) {
        size = getBase64Size(formData.image);
      }

      const url = editingFlag 
        ? `${API_BASE_URL}/api/admin/flags/${editingFlag._id}`
        : `${API_BASE_URL}/api/admin/flags`;
      const method = editingFlag ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          image: imageData,
          imageUrl: imageUrl,
          size: size
        }),
      });

      const data = await response.json();

      if (response.ok) {
        fetchFlags();
        resetForm();
        setMessage(`Flag ${editingFlag ? 'updated' : 'created'} successfully!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Error saving flag');
      }
    } catch (error) {
      console.error('Error saving flag:', error);
      setMessage('Error saving flag. Please try again.');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this flag? This will also remove it from any matches using it.')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/flags/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchFlags();
        setMessage('Flag deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting flag:', error);
    }
  };

  const handleEdit = (flag) => {
    setFormData({
      name: flag.name,
      image: flag.image,
      imageUrl: flag.imageUrl || ''
    });
    setEditingFlag(flag);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: '',
      imageUrl: ''
    });
    setShowAddForm(false);
    setEditingFlag(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select a valid image file');
      return;
    }

    setUploading(true);
    setMessage('Processing image...');

    try {
      const resizedImage = await resizeImage(file, 50); // Max 50KB
      setFormData({
        ...formData,
        image: resizedImage,
        imageUrl: '' // Clear URL if file is selected
      });
      setMessage('Image processed successfully!');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error processing image:', error);
      setMessage('Error processing image. Please try again.');
    }
    setUploading(false);
  };

  const handleUrlChange = (e) => {
    setFormData({
      ...formData,
      imageUrl: e.target.value,
      image: '' // Clear file if URL is entered
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Flag Management</h2>
          <p className="text-gray-600">Manage team flags for matches</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-sm font-medium w-full sm:w-auto"
        >
          {showAddForm ? 'Cancel' : '+ Add Flag'}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('Error') || message.includes('error')
          ? 'bg-red-50 text-red-700'
          : 'bg-green-50 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-6">
            {editingFlag ? 'Edit Flag' : 'Add New Flag'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Flag Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="e.g., Bangladesh, Brazil, Germany"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Flag Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max 50KB, will be automatically resized to 200x150
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Use Image URL
                </label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={handleUrlChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="https://example.com/flag.png"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Image will be downloaded and converted to base64
                </p>
              </div>
            </div>

            {/* Preview */}
            {(formData.image || formData.imageUrl) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview:
                </label>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img 
                    src={formData.image || formData.imageUrl}
                    alt="Flag preview" 
                    className="w-24 h-16 object-cover rounded border bg-white"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      setMessage('Invalid image URL or corrupted image');
                    }}
                  />
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={uploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 text-sm font-medium flex-1 sm:flex-none"
              >
                {uploading ? 'Processing...' : editingFlag ? 'Update Flag' : 'Create Flag'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 text-sm font-medium flex-1 sm:flex-none"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Flags List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Flags ({flags.length})</h3>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 text-sm mt-2">Loading flags...</p>
          </div>
        ) : flags.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">No flags found. Add your first flag!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Flag
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Size
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Created By
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Created
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flags.map((flag) => (
                  <tr key={flag._id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4">
                      <img 
                        src={flag.image}
                        alt={flag.name}
                        className="w-12 h-8 object-cover rounded border"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCA0OCAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjMyIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAyNEgyNFYyNEgyNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                        }}
                      />
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{flag.name}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                      {formatFileSize(flag.size || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {flag.createdBy?.username}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                      {new Date(flag.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <button
                          onClick={() => handleEdit(flag)}
                          className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(flag._id)}
                          className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlagManagement;
